import * as React from 'react';
import { AlertTriangle, Bell, FileCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export interface AlertNotificationItem {
  id: string;
  type: 'approval' | 'alert';
  severity?: 'critical' | 'high' | 'medium' | 'low';
  count: number;
  label: string;
  href?: string;
  onAction?: () => void;
  actionLabel?: string;
}

export interface AlertNotificationBarProps {
  items: AlertNotificationItem[];
  onDismiss?: () => void;
  className?: string;
}

export function AlertNotificationBar({ items, onDismiss, className }: AlertNotificationBarProps) {
  const hasItems = items.some((item) => item.count > 0);

  if (!hasItems) {
    return null;
  }

  const approvals = items.find((item) => item.type === 'approval' && item.count > 0);
  const criticalAlerts = items.find(
    (item) => item.type === 'alert' && item.severity === 'critical' && item.count > 0
  );
  const highAlerts = items.find(
    (item) => item.type === 'alert' && item.severity === 'high' && item.count > 0
  );

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-lg border px-4 py-2.5',
        'bg-gradient-to-r from-amber-50 to-rose-50 border-amber-200/60',
        'shadow-sm',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center gap-4 text-sm">
        {approvals && (
          <div className="flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-amber-600" aria-hidden="true" />
            <span className="font-medium text-amber-800">
              {approvals.count} Pending Approval{approvals.count !== 1 ? 's' : ''}
            </span>
            {approvals.onAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={approvals.onAction}
                className="h-7 px-2 text-xs font-semibold text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                {approvals.actionLabel ?? 'Review'}
              </Button>
            )}
          </div>
        )}

        {(approvals && (criticalAlerts || highAlerts)) && (
          <span className="text-slate-300" aria-hidden="true">│</span>
        )}

        {criticalAlerts && (
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
            </span>
            <span className="font-medium text-rose-700">
              {criticalAlerts.count} Critical Alert{criticalAlerts.count !== 1 ? 's' : ''}
            </span>
            {criticalAlerts.onAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={criticalAlerts.onAction}
                className="h-7 px-2 text-xs font-semibold text-rose-700 hover:text-rose-900 hover:bg-rose-100"
              >
                {criticalAlerts.actionLabel ?? 'View'}
              </Button>
            )}
          </div>
        )}

        {highAlerts && !criticalAlerts && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
            <span className="font-medium text-amber-700">
              {highAlerts.count} High Alert{highAlerts.count !== 1 ? 's' : ''}
            </span>
            {highAlerts.onAction && (
              <Button
                variant="ghost"
                size="sm"
                onClick={highAlerts.onAction}
                className="h-7 px-2 text-xs font-semibold text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                {highAlerts.actionLabel ?? 'View'}
              </Button>
            )}
          </div>
        )}
      </div>

      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  );
}

export interface AlertSummaryBadgeProps {
  criticalCount?: number;
  highCount?: number;
  mediumCount?: number;
  lowCount?: number;
  className?: string;
}

export function AlertSummaryBadge({
  criticalCount = 0,
  highCount = 0,
  mediumCount = 0,
  lowCount = 0,
  className,
}: AlertSummaryBadgeProps) {
  const total = criticalCount + highCount + mediumCount + lowCount;

  if (total === 0) {
    return (
      <span className={cn('text-xs text-emerald-600 font-medium', className)}>
        All clear
      </span>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5 text-xs', className)}>
      {criticalCount > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">
          {criticalCount} critical
        </span>
      )}
      {highCount > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
          {highCount} high
        </span>
      )}
      {(mediumCount > 0 || lowCount > 0) && criticalCount === 0 && highCount === 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
          {mediumCount + lowCount} other
        </span>
      )}
    </div>
  );
}
